import React, { useMemo, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SendIcon from "@mui/icons-material/Send";
import { MathJax } from "better-react-mathjax";


function safeHtml(html) {
  return { __html: String(html ?? "") };
}

export default function ExamQuestionBody({
  questions = [],
  currentIndex = 0,
  answers = {},
  flags = {},
  currentQ,
  setIndexSafely,
  handleSelectOption,
  toggleFlag,
  saving = false,
  navBusy = false,
  submittingUI = false,
  canSubmit = false,
  setConfirmSubmit,
}) {
  const uiLocked = saving || navBusy || submittingUI;
  const submitBusy = submittingUI;

  const clickGuardRef = useRef(false);
const mathRef = useRef(null);

  const total = questions.length;
  const qid = currentQ?.id ?? null;
  const selected = qid ? answers[qid] : null;
  const isFlagged = qid ? !!flags[qid] : false;

  const options = useMemo(() => {
	  
	  
	  
    if (!currentQ) return [];
    if (Array.isArray(currentQ.options)) return currentQ.options;

    return [
      ["A", currentQ.option_a],
      ["B", currentQ.option_b],
      ["C", currentQ.option_c],
      ["D", currentQ.option_d],
      ["E", currentQ.option_e],
    ]
      .filter(([, v]) => v != null && String(v).trim() !== "")
      .map(([label, text]) => ({ label, text }));
  }, [currentQ]);

  const answeredCount = useMemo(
    () =>
      Object.values(answers || {}).filter(
        (v) => v != null && String(v).trim() !== ""
      ).length,
    [answers]
  );

  const block = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };



  if (!currentQ) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography>No question loaded.</Typography>
      </Paper>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* ================= QUESTION AREA ================= */}
      <Grid item xs={12} md={8}>
        <Paper
          sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}
          onCopy={block}
          onCut={block}
          onContextMenu={block}
        >
          {/* ===== TOP BAR ===== */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
          >
            <Typography fontWeight={800}>
              Question {currentIndex + 1} / {total}
            </Typography>

            <Box display="flex" gap={1} alignItems="center">
              <Chip
                label={`${answeredCount} answered`}
                size="small"
                sx={{ fontWeight: 700 }}
              />

              <Button
                variant={isFlagged ? "contained" : "outlined"}
                onClick={toggleFlag}
                disabled={uiLocked}
                startIcon={<FlagIcon />}
                sx={{ fontWeight: 800 }}
              >
                {isFlagged ? "Flagged" : "Flag"}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* ===== QUESTION TEXT ===== */}
         <Box sx={{ mb: 2 }} ref={mathRef}>

            {currentQ.passage && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 2,
                  backgroundColor: "#f9fafb",
                }}
              >
                <Typography
                  component="div"
                  sx={{ fontSize: 14, lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={safeHtml(currentQ.passage)}
                />
              </Paper>
            )}

          <MathJax dynamic>
  <Typography component="div" sx={{ fontSize: 16, lineHeight: 1.8 }}>
    {currentQ.question || currentQ.question_text}
  </Typography>
</MathJax>

          </Box>


          {/* ===== IMAGES ===== */}

{currentQ.stem_image_url && (
  <Box
    sx={{
      mt: 2,
      mb: 2.5,
      display: "flex",
      justifyContent: "center",
    }}
  >
    <img
      src={currentQ.stem_image_url}
      alt="Question illustration"
      loading="lazy"
      draggable={false}
      style={{
        maxWidth: "100%",
        maxHeight: 380,
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        userSelect: "none",
      }}
    />
  </Box>
)}






          {/* ===== OPTIONS ===== */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}>
            {options.map((opt) => {
              const isSelected = selected === opt.label;

              return (
                <Button
                  key={opt.label}
                  disabled={uiLocked}
                  onClick={() => {
                    if (clickGuardRef.current) return;
                    clickGuardRef.current = true;
                    handleSelectOption(opt.label);
                    setTimeout(() => {
                      clickGuardRef.current = false;
                    }, 180);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    px: 2,
                    py: 1.6,
                    borderRadius: 3,
                    border: "1.8px solid",
                    gap: 1.5,
                    transition: "all 0.18s ease",

                    ...(!isSelected && {
                      backgroundColor: "#ffffff",
                      borderColor: "#e0e7ff",
                      color: "#1e3a8a",
                      "&:hover": {
                        backgroundColor: "#f5f9ff",
                        borderColor: "#93c5fd",
                      },
                    }),

                    ...(isSelected && {
                      backgroundColor: "#1e40af",
                      borderColor: "#1e40af",
                      color: "#ffffff",
                      boxShadow: "0 6px 18px rgba(30,64,175,0.35)",
                    }),
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 36,
                      height: 36,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 900,
                      fontSize: 15,
                      backgroundColor: isSelected
                        ? "rgba(255,255,255,0.25)"
                        : "#eef2ff",
                      color: isSelected ? "#fff" : "#1e40af",
                    }}
                  >
                    {opt.label}
                  </Box>
<Box sx={{ flex: 1 }} >
<MathJax dynamic>
  <Typography component="div">
    {opt.text}
  </Typography>
</MathJax>

</Box>

				  
				  
				  
                </Button>
              );
            })}
          </Box>

          {/* ===== RESPONSIVE NAVIGATION ===== */}
          <Box
            sx={{
              mt: 3,
              position: { xs: "sticky", md: "static" },
              bottom: 0,
              zIndex: 20,
              backgroundColor: "background.paper",
              borderTop: { xs: "1px solid #e5e7eb", md: "none" },
              py: { xs: 1, md: 0 },
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={uiLocked || currentIndex <= 0}
                  startIcon={<NavigateBeforeIcon />}
                  onClick={() => setIndexSafely(currentIndex - 1)}
                  sx={{ fontWeight: 900 }}
                >
                  Prev
                </Button>
              </Grid>

              <Grid item xs={4}>
                <Tooltip
                  title={
                    !canSubmit
                      ? "Answer at least one question to submit"
                      : ""
                  }
                >
                  <span>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      disabled={!canSubmit || uiLocked}
                      startIcon={
                        submitBusy ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SendIcon />
                        )
                      }
                      onClick={() => setConfirmSubmit(true)}
                      sx={{ fontWeight: 900 }}
                    >
                      Submit
                    </Button>
                  </span>
                </Tooltip>
              </Grid>

              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={uiLocked || currentIndex >= total - 1}
                  endIcon={<NavigateNextIcon />}
                  onClick={() => setIndexSafely(currentIndex + 1)}
                  sx={{ fontWeight: 900 }}
                >
                  Next
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      {/* ================= DESKTOP QUESTION MAP ================= */}
      <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }}>
        <Paper sx={{ p: 2, borderRadius: 3, position: "sticky", top: 12 }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>
            Question Map
          </Typography>

          <Grid container spacing={1}>
            {questions.map((q, i) => {
              const hasAns =
                answers?.[q.id] != null &&
                String(answers[q.id]).trim() !== "";
              const isF = !!flags[q.id];
              const isActive = i === currentIndex;

              return (
                <Grid item xs={3} key={q.id}>
                  <Button
                    fullWidth
                    variant={isActive ? "contained" : "outlined"}
                    disabled={uiLocked}
                    onClick={() => setIndexSafely(i)}
                    sx={{
                      fontWeight: 900,
                      minHeight: 42,
                      borderRadius: 2,

                      ...(isActive && {
                        backgroundColor: "#1e40af",
                        color: "#ffffff",
                      }),

                      ...(!isActive && hasAns && {
                        backgroundColor: "#dcfce7",
                        color: "#065f46",
                      }),

                      ...(!isActive && !hasAns && {
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                      }),

                      ...(isF && {
                        boxShadow: "0 0 0 2px #fb923c inset",
                      }),
                    }}
                  >
                    {i + 1}
                    {isF && <FlagIcon sx={{ fontSize: 14, ml: 0.5 }} />}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
